// Customers (CRM) data: Firestore listeners and the manager's writes.
//
// Everything shown on the page (families, lifecycle, lifetime value, duplicate suggestions) is computed in the
// browser by the pure module shared with the functions project (functions/src/crm). Only decisions are stored:
// customers/{id} and crmRejectedPairs/{id}, both manager-only in the Firestore rules.

import { collection, doc, onSnapshot, writeBatch } from "firebase/firestore";
import { db } from "../firebase-config";
import { stub } from "./crmStub";
import type { CrmInput, CustomerDoc, RejectedPair } from "../../../preschool.bee-happy.club_functions/functions/src/crm/crm";

export * from "../../../preschool.bee-happy.club_functions/functions/src/crm/crm";

type Unsubscribe = () => void;

/** `npm run dev` uses in-memory mock data (crmStub.ts) instead of Firestore. */
const DEV = import.meta.env.DEV;

const SOURCES: { key: keyof CrmInput; collection: string; map?: (id: string, data: Record<string, unknown>) => unknown }[] = [
  { key: "children", collection: "children" },
  { key: "registrations", collection: "registrations" },
  { key: "holidayTerms", collection: "holidayTerms" },
  { key: "holidayRegistrations", collection: "holidayRegistrations" },
  { key: "holidayInvoices", collection: "holidayInvoices" },
  { key: "preschoolInvoices", collection: "preschoolInvoices", map: (id, data) => ({ ...data, ref: data.ref ?? id }) },
  { key: "customers", collection: "customers" },
  { key: "rejectedPairs", collection: "crmRejectedPairs" },
];

/**
 * Listens to every collection the CRM reads and emits the combined input once all have answered. A collection that
 * cannot be read (e.g. rules not deployed yet) counts as empty and is reported through onError.
 */
export const watchCrmInput = (onData: (input: CrmInput) => void, onError: (source: string, error: Error) => void): Unsubscribe => {
  if (DEV) return stub().watch(onData);
  const input = Object.fromEntries(SOURCES.map((s) => [s.key, []])) as unknown as CrmInput;
  const ready = new Set<string>();
  const emit = () => {
    if (ready.size === SOURCES.length) onData({ ...input });
  };
  const stops = SOURCES.map((source) => onSnapshot(collection(db, source.collection), (snapshot) => {
    (input as unknown as Record<string, unknown[]>)[source.key] = snapshot.docs.map((d) =>
      source.map ? source.map(d.id, d.data()) : { ...d.data(), id: d.id });
    ready.add(source.key);
    emit();
  }, (error) => {
    ready.add(source.key);
    onError(source.collection, error);
    emit();
  }));
  return () => stops.forEach((stop) => stop());
};

export interface DocChange {
  id: string;
  /** null deletes the document */
  doc: CustomerDoc | null;
}

/** Firestore refuses undefined values. */
const clean = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

/** Writes customer documents and rejected pairs in one batch. */
export const saveCrmChanges = async (changes: { customers?: DocChange[]; pairs?: { id: string; pair: RejectedPair | null }[] }) => {
  if (DEV) return stub().save(changes);
  const batch = writeBatch(db);
  for (const change of changes.customers ?? []) {
    const ref = doc(db, "customers", change.id);
    if (change.doc) batch.set(ref, clean(change.doc));
    else batch.delete(ref);
  }
  for (const change of changes.pairs ?? []) {
    const ref = doc(db, "crmRejectedPairs", change.id);
    if (change.pair) batch.set(ref, clean(change.pair));
    else batch.delete(ref);
  }
  await batch.commit();
};

/** A new random customer document id. */
export const newCustomerId = () => (DEV ? stub().newId() : doc(collection(db, "customers")).id);

/** Today as YYYY-MM-DD in the browser's local time. */
export const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};
